'use client';

import { useEffect, useState } from 'react';

export interface TocItem {
  id: string;
  label: string;
}

interface FloatingTocProps {
  items: readonly TocItem[];
  /** Heading above the list. Callers pass the localized string. */
  title?: string;
}

export const FloatingToc = ({
  items,
  title = 'On this page',
}: FloatingTocProps) => {
  const [activeId, setActiveId] = useState<string>(items[0]?.id ?? '');

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const sections = items
      .map((item) => document.getElementById(item.id))
      .filter((el): el is HTMLElement => el !== null);

    if (sections.length === 0) return;

    /**
     * We track which section is "current" by finding the last section whose
     * top has crossed an offset line near the top of the viewport. This
     * matches the way docs sites highlight TOC items: the active item is the
     * heading you've most recently scrolled past, not the heading nearest
     * the bottom of the viewport.
     */
    const update = () => {
      const offset = 140; // matches scroll-mt-24 + a bit of breathing room
      let current = sections[0].id;
      for (const section of sections) {
        const rect = section.getBoundingClientRect();
        if (rect.top - offset <= 0) {
          current = section.id;
        } else {
          break;
        }
      }
      setActiveId(current);
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [items]);

  return (
    <aside className="hidden lg:block">
      <nav
        aria-label={title}
        className="sticky top-28 max-h-[calc(100vh-8rem)] overflow-y-auto"
      >
        <p className="mb-3 text-[10px] font-medium uppercase tracking-[0.18em] text-neutral-400">
          {title}
        </p>
        <ul className="space-y-0.5 text-sm">
          {items.map((item) => {
            const isActive = item.id === activeId;
            return (
              <li key={item.id}>
                <a
                  href={`#${item.id}`}
                  aria-current={isActive ? 'true' : undefined}
                  className={[
                    'group relative block py-1.5 pl-4 transition-colors duration-200',
                    isActive
                      ? 'text-neutral-900'
                      : 'text-neutral-500 hover:text-neutral-900',
                  ].join(' ')}
                >
                  <span
                    aria-hidden
                    className={[
                      'absolute left-0 top-1/2 h-4 w-px -translate-y-1/2 transition-all duration-200',
                      isActive
                        ? 'bg-neutral-900'
                        : 'bg-neutral-200 group-hover:bg-neutral-400',
                    ].join(' ')}
                  />
                  {item.label}
                </a>
              </li>
            );
          })}
        </ul>
      </nav>
    </aside>
  );
};
